import { useState } from "react";
import { toast } from "sonner";
import { Trash2, Pencil } from "lucide-react";
import { api, API, errorText } from "@/lib/api";
import { Field, Panel, field } from "@/components/admin/shared";
import { BeforeAfterSlider } from "@/components/gallery/BeforeAfterSlider";

const src = (u) => `${API.replace(/\/api$/, "")}${u}`;
const EMPTY = { before_id: "", after_id: "", service: "", brand: "", model: "", description: "", status: "draft" };

const PhotoPick = ({ photos, value, onChange, label, id }) => (
  <label className="block text-xs uppercase tracking-wider text-mute">{label}
    <select className={`${field} mt-1 normal-case tracking-normal`} value={value} onChange={(e) => onChange(e.target.value)} data-testid={id}>
      <option value="">Elige una foto…</option>
      {photos.map((p) => <option key={p.id} value={p.id}>{p.title || p.alt || p.slug}</option>)}
    </select>
  </label>
);

export const BeforeAfterManager = ({ photos, pairs, onChange }) => {
  const [f, setF] = useState(EMPTY);
  const [editing, setEditing] = useState(null);
  const byId = Object.fromEntries(photos.map((p) => [p.id, p]));
  const save = async (status) => {
    try {
      const body = { ...f, status };
      if (editing) await api.put(`/admin/gallery/before-after/${editing}`, body); else await api.post("/admin/gallery/before-after", body);
      toast.success(status === "published" ? "Publicado en la galería" : "Guardado como borrador");
      setF(EMPTY); setEditing(null); onChange();
    } catch (e) { toast.error(errorText(e)); }
  };
  const del = async (id) => {
    if (!window.confirm("¿Eliminar este antes y después? Las fotos no se borran.")) return;
    try { await api.delete(`/admin/gallery/before-after/${id}`); toast.success("Eliminado"); } catch (e) { toast.error(errorText(e)); }
    onChange();
  };
  const t = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const b = byId[f.before_id], a = byId[f.after_id];
  return (
    <Panel title="Antes y después de la reparación">
      <div className="grid gap-4 md:grid-cols-2">
        <div className="space-y-3">
          <div className="grid gap-3 sm:grid-cols-2">
            <PhotoPick photos={photos} value={f.before_id} onChange={(v) => setF({ ...f, before_id: v })} label="Foto anterior" id="ba-before-select" />
            <PhotoPick photos={photos} value={f.after_id} onChange={(v) => setF({ ...f, after_id: v })} label="Foto posterior" id="ba-after-select" />
          </div>
          <Field id="ba-service" label="Servicio" value={f.service} onChange={t("service")} />
          <div className="grid gap-3 sm:grid-cols-2"><Field id="ba-brand" label="Marca" value={f.brand} onChange={t("brand")} /><Field id="ba-model" label="Modelo" value={f.model} onChange={t("model")} /></div>
          <label className="block text-xs uppercase tracking-wider text-mute">Descripción del trabajo<textarea className={`${field} mt-1`} rows={3} value={f.description} onChange={t("description")} data-testid="ba-description" /></label>
          <div className="flex flex-wrap gap-2">
            <button className="btn-ghost" onClick={() => save("draft")} data-testid="ba-save-draft-btn">Guardar borrador</button>
            <button className="btn-gold" onClick={() => save("published")} data-testid="ba-publish-btn">Publicar</button>
            {editing && <button className="btn-ghost" onClick={() => { setF(EMPTY); setEditing(null); }}>Cancelar</button>}
          </div>
          <p className="text-xs text-mute">Tip: sube las 2 fotos arriba y déjalas en borrador si no quieres que también salgan sueltas en la galería.</p>
        </div>
        <div>{b && a ? <BeforeAfterSlider before={{ ...b, src: src(b.url) }} after={{ ...a, src: src(a.url) }} id="preview" /> : <div className="flex aspect-[4/3] items-center justify-center rounded-sm border border-dashed border-line text-sm text-mute">Vista previa del comparador</div>}</div>
      </div>
      {pairs.length > 0 && (
        <ul className="mt-6 divide-y divide-line text-sm">
          {pairs.map((p) => (
            <li key={p.id} className="flex items-center gap-3 py-2" data-testid={`ba-item-${p.id}`}>
              <span className={`rounded-full px-2 py-0.5 text-xs font-bold ${p.status === "published" ? "bg-green text-white" : "bg-surface text-mute"}`}>{p.status === "published" ? "Publicado" : "Borrador"}</span>
              <span className="flex-1">{p.service} {[p.brand, p.model].filter(Boolean).join(" ")}</span>
              <button onClick={() => { setF({ ...EMPTY, ...p }); setEditing(p.id); }} aria-label="Editar" data-testid={`ba-edit-${p.id}`}><Pencil size={14} /></button>
              <button onClick={() => del(p.id)} aria-label="Eliminar" className="text-red-600" data-testid={`ba-delete-${p.id}`}><Trash2 size={14} /></button>
            </li>
          ))}
        </ul>
      )}
    </Panel>
  );
};
